import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';

// Models and services
import { 
  ProductSkuResponse, 
  ProductResponse, 
  ListProductSkusResponse
} from '../../models';
import { ProductSkuService } from '../../services/product-sku.service';
import { CommonService, ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';

// Import sub-components
import { SkuFormComponent } from '../sku-form/sku-form.component';
import { SelectModule } from 'primeng/select';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';

@Component({
  selector: 'lib-sku-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    SkuFormComponent,
    CurrencyformatPipe
],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.Default,
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '1200px', height: '90vh' }"
      (onHide)="close()">
    
      <ng-template pTemplate="header">
        <div class="flex align-items-center gap-3">
          <div class="flex align-items-center gap-3">
            @if (product()?.image_url) {
              <img
                [src]="product()!.image_url"
                [alt]="product()!.name_primary_language"
                class="w-3rem h-3rem border-round object-cover" />
            }
            <div>
              <h3 class="m-0 text-lg font-semibold">{{ 'RETAIL_BILLING.SKU.MANAGE' | translate }}</h3>
              <div class="text-sm text-500 mt-1">
                <span class="font-medium">{{ product()?.name_primary_language }}</span>
                @if (product()?.category?.name_primary_language) {
                  <span class="ml-2">• {{ product()?.category?.name_primary_language }}</span>
                }
              </div>
            </div>
          </div>
        </div>
      </ng-template>
    
      <div class="card">
        <div class="flex justify-content-between align-items-center mb-4">
          <div class="flex gap-3 align-items-center">
            <p-iconField iconPosition="left">
              <p-inputIcon styleClass="pi pi-search" />
              <input
                pInputText
                type="text"
                [placeholder]="'common.PLACEHOLDER.SEARCH' | translate"
                [(ngModel)]="searchTerm"
                (ngModelChange)="onSearch()"
                aria-label="search-skus-input" />
              </p-iconField>
    
              <p-select [options]="statusFilterOptions"
                [(ngModel)]="selectedStatusFilter"
                [placeholder]="'RETAIL_BILLING.SKU.FILTER_BY_STATUS' | translate"
                optionLabel="label"
                optionValue="value"
                [showClear]="true"
                appendTo="body"
                (onChange)="onStatusFilterChange()"
                [style]="{ minWidth: '200px' }"
                data-testid="status-filter-dropdown" />
            </div>
    
            <p-button [label]="'RETAIL_BILLING.SKU.CREATE' | translate"
              icon="pi pi-plus"
              (onClick)="showCreateDialog()"
              aria-label="create-sku-button" />
          </div>
    
          <p-table
            [value]="skus"
            [loading]="loading"
            [paginator]="true"
            [lazy]="true"
            [rows]="pageSize"
            [(first)]="first"
            [totalRecords]="totalRecords"
            (onPage)="onPageChange($event)"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            [rowsPerPageOptions]="[10, 25, 50]"
            paginatorDropdownAppendTo="body"
            styleClass="p-datatable-gridlines">
    
            <ng-template pTemplate="header">
              <tr>
                <th>{{ 'RETAIL_BILLING.SKU.SKU_NAME' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.SKU.SKU_CODE' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.SKU.BASE_PRICE' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.SKU.ONLINE_PRICE' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.SKU.SUPPLIER' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.SKU.SHIPPING_WEIGHT' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.SKU.ATTRIBUTES' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.PRODUCTS.STATUS' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.COMMON.ACTIONS' | translate }}</th>
              </tr>
            </ng-template>
    
            <ng-template pTemplate="body" let-sku>
              <tr>
                <td>
                  <div class="flex align-items-center">
                    @if (sku.image_url) {
                      <img
                        [src]="sku.image_url"
                        [alt]="sku.variant_name_primary_language"
                        class="w-3rem h-3rem mr-2 border-round" />
                    }
                    <div>
                      <div class="font-medium">{{ sku.variant_name_primary_language }}</div>
                      <div class="text-sm text-500">{{ sku.variant_name_secondary_language }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="font-mono text-sm">{{ sku.sku_code }}</span>
                </td>
                <td>
                  <span class="font-semibold">{{ sku.unit_price_minor | currencyformat }}</span>
                </td>
                <td>
                  @if (sku.online_price_minor != null) {
                    <span class="font-semibold">{{ sku.online_price_minor | currencyformat }}</span>
                  }
                </td>
                <td>
                  <span>{{ sku.supplier_name_primary || sku.supplier_name_secondary || '—' }}</span>
                </td>
                <td>
                  <span>{{ sku.shipping_weight ?? '—' }}</span>
                </td>
                <td>
                  <div  class="flex gap-1 flex-wrap">
                    {{ sku.attributes_json.length || 0 }}
                  </div>
                </td>
                <td>
                  <div class="flex align-items-center gap-2">
                    <p-tag [value]="sku.is_active ? ('RETAIL_BILLING.PRODUCTS.ACTIVE' | translate) : ('RETAIL_BILLING.PRODUCTS.INACTIVE' | translate)"
                      [severity]="sku.is_active ? 'success' : 'danger'" />
                    @if (sku.product_category_is_active === false) {
                      <i
                        class="pi pi-exclamation-triangle text-orange-500 cursor-pointer"
                        [pTooltip]="'RETAIL_BILLING.PRODUCTS.INACTIVE_CATEGORY_POS_WARNING' | translate"
                        tooltipPosition="top"
                        aria-label="inactive-category-pos-warning"></i>
                    }
                  </div>
                </td>
                <td>
                  <div class="flex gap-2">
                    <p-button [icon]="sku.is_active ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                      [pTooltip]="sku.is_active ? ('RETAIL_BILLING.COMMON.MARK_INACTIVE' | translate) : ('RETAIL_BILLING.COMMON.MARK_ACTIVE' | translate)"
                      tooltipPosition="top"
                      [text]="true"
                      [rounded]="true"
                      [outlined]="true"
                      size="small"
                      [severity]="sku.is_active ? 'success' : 'danger'"
                      (onClick)="toggleSkuStatus(sku)"
                      [ariaLabel]="'toggle-status-sku-' + sku.id" />
                    <p-button icon="pi pi-pencil"
                      [pTooltip]="'RETAIL_BILLING.COMMON.EDIT' | translate"
                      tooltipPosition="top"
                      [text]="true"
                      [rounded]="true"
                      [outlined]="true"
                      size="small"
                      (onClick)="editSku(sku)"
                      [ariaLabel]="'edit-sku-' + sku.id" />
                  </div>
                </td>
              </tr>
            </ng-template>
    
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6" class="text-center">{{ 'RETAIL_BILLING.SKU.NO_SKUS' | translate }}</td>
              </tr>
            </ng-template>
          </p-table>
        </div>
    
        <ng-template pTemplate="footer">
          <div class="flex justify-content-end">
            <p-button [label]="'RETAIL_BILLING.COMMON.CLOSE' | translate"
              icon="pi pi-times"
              [text]="true"
              (onClick)="close()"
              aria-label="close-button" />
          </div>
        </ng-template>
      </p-dialog>
    
      <!-- Create/Edit SKU Dialog -->
      @if (showSkuDialog) {
        <lib-sku-form [visible]="showSkuDialog"
          [sku]="selectedSku"
          [product]="product()"
          [tenantId]="tenantId()"
          [companyLevel]="companyLevel()"
          [primaryLanguage]="primaryLanguage()"
          [secondaryLanguage]="secondaryLanguage()"
          [isSecondaryLanguage]="isSecondaryLanguage()"
          (onSave)="onSkuSaved($event)"
          (onCancel)="hideSkuDialog()" />
      }
    
      <p-confirmDialog />
      <p-toast />
    `,
})
export class SkuListComponent implements OnInit, OnDestroy {
  private skuService = inject(ProductSkuService);
  private toastService = inject(ToastMessageService);
  private confirmationService = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private commonService = inject(CommonService);
  private store = inject(Store);

  readonly visible = input(false);
  readonly product = input<ProductResponse | null>(null);
  readonly tenantId = input<number>();
  readonly companyLevel = input<boolean>(true);

  localTenantId: number = 0;
  readonly primaryLanguage = input<string>('English');
  readonly secondaryLanguage = input<string>('Arabic');
  readonly isSecondaryLanguage = input<boolean>(false);
  
  readonly visibleChange = output<boolean>();
  readonly onClose = output<void>();

  skus: ProductSkuResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  pageNumber = 1;
  first = 0;
  
  searchTerm = '';
  selectedStatusFilter: boolean | null = null;
  statusFilterOptions = [
    { label: '', value: true },
    { label: '', value: false }
  ];
  
  showSkuDialog = false;
  selectedSku: ProductSkuResponse | null = null;
  requestParams = new Map();
  private destroy$ = new Subject<void>();

  ngOnInit() {
      this.requestParams.set('PageNumber', { name: 'PageNumber', value: this.pageNumber });
      this.requestParams.set('PageSize', { name: 'PageSize', value: this.pageSize });
      this.initializeStatusFilterOptions();
  }

  private resetPagination(): void {
    this.pageNumber = 1;
    this.first = 0;
    this.requestParams.set('PageNumber', { name: 'PageNumber', value: this.pageNumber });
    this.requestParams.set('PageSize', { name: 'PageSize', value: this.pageSize });
  }

  initializeStatusFilterOptions() {
    this.statusFilterOptions = [
      { 
        label: this.translate.instant('RETAIL_BILLING.PRODUCTS.ACTIVE'), 
        value: true 
      },
      { 
        label: this.translate.instant('RETAIL_BILLING.PRODUCTS.INACTIVE'), 
        value: false 
      }
    ];

    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tenant: TENANTSINTERFACE) => {
          this.localTenantId = tenant.selected_tenant.id;
          if (this.localTenantId > 0) {
            this.store.dispatch({ type: 'ROUTER_GO' });
            this.loadSkus();
          }
        }
      });
  }


  loadSkus() {
    const product = this.product();
    if (!product) return;
    
    this.loading = true;

    // Clear previous search/filter params
    this.requestParams.delete('search_term');
    this.requestParams.delete('is_active');

    // Add search term if exists
    if(this.searchTerm) {
      this.requestParams.set('search_term', { name: 'search_term', value: this.searchTerm });
    }

    // Add status filter if selected
    if(this.selectedStatusFilter !== null) {
      this.requestParams.set('is_active', { name: 'is_active', value: this.selectedStatusFilter });
    }

    const loadFn = this.companyLevel()
      ? this.skuService.getCompanyProductSkus(product.id, this.requestParams)
      : this.skuService.getTenantProductSkus(this.tenantId()!, product.id, this.requestParams);

    loadFn
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListProductSkusResponse) => {
          this.skus = response.items || [];
          this.totalRecords = response.total_items;
          if (response.page_size) {
            this.pageSize = response.page_size;
          }
          if (response.current_page) {
            this.pageNumber = response.current_page;
            this.first = (response.current_page - 1) * this.pageSize;
          }
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  onSearch() {
    this.resetPagination();
    this.loadSkus();
  }

  onStatusFilterChange() {
    this.resetPagination();
    this.loadSkus();
  }

  onPageChange(e: { first: number; rows: number }) {
    this.pageSize = e.rows;
    this.first = e.first;
    this.pageNumber = e.first ? e.first / this.pageSize + 1 : 1;
    this.requestParams.set('PageNumber', { name: 'PageNumber', value: this.pageNumber });
    this.requestParams.set('PageSize', { name: 'PageSize', value: this.pageSize });
    this.loadSkus();
  }

  showCreateDialog() {
    this.selectedSku = null;
    this.showSkuDialog = true;
  }

  editSku(sku: ProductSkuResponse) {
    this.selectedSku = sku;
    this.showSkuDialog = true;
  }

  toggleSkuStatus(sku: ProductSkuResponse) {
    const newStatus = !sku.is_active;
    const confirmMessage = newStatus 
      ? this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_ACTIVATE')
      : this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_DEACTIVATE');
    
    this.confirmationService.confirm({
      message: confirmMessage,
      header: this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const product = this.product();
        if (!product) return;
        
        const updatedSku = {
          ...sku,
          is_active: newStatus
        };
        
        const updateFn = this.companyLevel()
          ? this.skuService.updateCompanyProductSku(product.id, sku.id, updatedSku)
          : this.skuService.updateTenantProductSku(this.tenantId()!, product.id, sku.id, updatedSku);
        
        updateFn
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const successMessage = newStatus
                ? this.translate.instant('RETAIL_BILLING.SKU.ACTIVATED_SUCCESS')
                : this.translate.instant('RETAIL_BILLING.SKU.DEACTIVATED_SUCCESS');
              this.toastService.showSuccess(successMessage);
              this.loadSkus();
            },
            error: (error) => {
              this.toastService.showError(error);
            }
          });
      }
    });
  }

  onSkuSaved(sku: ProductSkuResponse) {
    this.hideSkuDialog();
    this.loadSkus();
    const message = this.selectedSku 
      ? 'RETAIL_BILLING.SKU.UPDATE_SUCCESS'
      : 'RETAIL_BILLING.SKU.CREATE_SUCCESS';
    this.toastService.showSuccess(this.translate.instant(message));
  }

  hideSkuDialog() {
    this.showSkuDialog = false;
    this.selectedSku = null;
  }

  close(): void {
    this.onClose.emit();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}